import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Client, type ContentType } from '@modyo/sdk';
import { environment } from '@env/environment';
import { ModyoConfig, ModyoAuthOptions, ModyoContentOptions } from './modyo.types';

@Injectable({ providedIn: 'root' })
export class ServicioClienteModyo {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly esServidor = isPlatformServer(this.platformId);

  private readonly cliente: Client;
  private readonly espacioPorDefecto?: string;
  private dominio?: string;
  private token?: string;

  constructor() {
    const configuracion = environment.modyoConfig as ModyoConfig | undefined;

    if (!configuracion) {
      throw new Error('La configuración de Modyo no está definida.');
    }

    const idioma = configuracion.locale ?? 'es';

    const baseUrl = configuracion.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');

    if (!baseUrl) {
      throw new Error('La URL base de Modyo no está configurada.');
    }

    this.cliente = new Client(baseUrl, idioma);
    this.espacioPorDefecto = configuracion.spaceId;

    if (configuracion.apiToken) {
      this.establecerAutenticacion({ token: configuracion.apiToken });
    }
  }

  public establecerAutenticacion({ realm: dominio, token }: ModyoAuthOptions): void {
    this.dominio = dominio ?? undefined;
    this.token = token ?? undefined;
  }

  public obtenerCliente(): Client {
    return this.cliente;
  }

  public esContextoServidor(): boolean {
    return this.esServidor;
  }

  public obtenerTipoContenido<TEntrada = unknown>(
    uidTipo: string,
    opciones: ModyoContentOptions = {},
  ): ContentType<TEntrada> {
    const uidEspacio = opciones.spaceUid ?? this.espacioPorDefecto;

    if (!uidEspacio) {
      throw new Error('Se requiere un UID de espacio de Modyo para obtener contenido.');
    }

    const dominio = opciones.realm ?? this.dominio;
    const token = opciones.token ?? this.token;

    if (dominio && !token) {
      throw new Error('Se requiere un token de usuario al solicitar contenido privado de Modyo.');
    }

    return this.cliente.getContentType<TEntrada>(uidEspacio, uidTipo, dominio, token);
  }
}
